import { useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/api/client';
import { getHighLevelContext } from '@/utils/highlevel';
import toast from 'react-hot-toast';

// Session storage key for showing toast after reload
const OAUTH_SUCCESS_KEY = 'highlevel_oauth_success';

export function useHighLevelAuth() {
  const queryClient = useQueryClient();
  const context = getHighLevelContext();
  const locationId = context?.locationId || '';

  // Debug: log the locationId being used
  console.log('useHighLevelAuth - context:', context, 'locationId:', locationId);

  // Show toast if we just completed OAuth and reloaded
  useEffect(() => {
    const pendingSuccess = sessionStorage.getItem(OAUTH_SUCCESS_KEY);
    if (pendingSuccess) {
      sessionStorage.removeItem(OAUTH_SUCCESS_KEY);
      toast.success('Successfully connected to CRM!');

      // Auto-refresh HighLevel entity counts after OAuth reload
      if (locationId) {
        apiClient.refreshEntityCounts(locationId, { system: 'highlevel' })
          .then(() => {
            queryClient.invalidateQueries({ queryKey: ['sync-status', locationId] });
            console.log('HighLevel entity counts refreshed after connection');
          })
          .catch((err) => {
            console.warn('Failed to refresh HighLevel counts after connection:', err);
          });
      }
    }
  }, [locationId, queryClient]);

  // Listen for OAuth success messages from popup
  useEffect(() => {
    const handleMessage = async (event: MessageEvent) => {
      // Handle OAuth success message from popup
      if (event.data?.type === 'OAUTH_SUCCESS' && event.data?.provider === 'highlevel') {
        console.log('Received OAuth success message:', event.data);

        // If the OAuth callback returned a real locationId, update sessionStorage
        // This is important because in dev mode we may have a fallback locationId
        const realLocationId = event.data.locationId;
        if (realLocationId && realLocationId !== locationId) {
          console.log('Updating sessionStorage with real locationId from OAuth:', realLocationId);
          // Get existing context and update the locationId
          const existingContext = sessionStorage.getItem('highlevel_context');
          const storedContext = existingContext ? JSON.parse(existingContext) : {};
          storedContext.locationId = realLocationId;
          sessionStorage.setItem('highlevel_context', JSON.stringify(storedContext));

          // Mark that we should show success toast after reload
          sessionStorage.setItem(OAUTH_SUCCESS_KEY, 'true');

          // Force a page reload to pick up the new locationId
          // This ensures all hooks get the correct value
          window.location.reload();
          return;
        }

        // Invalidate and refetch the connection status
        queryClient.invalidateQueries({
          queryKey: ['highlevel-connection', locationId],
        });
        toast.success('Successfully connected to CRM!');

        // Auto-refresh HighLevel entity counts after successful connection
        try {
          await apiClient.refreshEntityCounts(locationId, { system: 'highlevel' });
          queryClient.invalidateQueries({ queryKey: ['sync-status', locationId] });
          console.log('HighLevel entity counts refreshed after connection');
        } catch (err) {
          console.warn('Failed to refresh HighLevel counts after connection:', err);
        }
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [queryClient, locationId]);

  // Get connection status
  const {
    data: connection,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['highlevel-connection', locationId],
    queryFn: () => apiClient.getHighLevelConnection(locationId),
    enabled: !!locationId,
    refetchInterval: 30000, // Refetch every 30 seconds
  });

  // Connect to HighLevel
  const connectMutation = useMutation({
    mutationFn: async () => {
      const url = await apiClient.getHighLevelAuthUrl(locationId);
      // Open OAuth popup
      const width = 600;
      const height = 700;
      const left = window.screen.width / 2 - width / 2;
      const top = window.screen.height / 2 - height / 2;

      const popup = window.open(
        url,
        'CRM Authorization',
        `width=${width},height=${height},left=${left},top=${top}`
      );

      // Poll for popup close as fallback
      return new Promise<void>((resolve, reject) => {
        const checkPopup = setInterval(() => {
          if (popup?.closed) {
            clearInterval(checkPopup);
            // Refetch connection status (message handler may have already done this)
            queryClient.invalidateQueries({
              queryKey: ['highlevel-connection', locationId],
            });
            resolve();
          }
        }, 500);

        // Timeout after 5 minutes
        setTimeout(() => {
          clearInterval(checkPopup);
          if (popup && !popup.closed) {
            popup.close();
          }
          reject(new Error('OAuth timeout'));
        }, 300000);
      });
    },
    onSuccess: () => {
      // Toast is now shown by the message handler
    },
    onError: (error) => {
      toast.error(`Failed to connect: ${error.message}`);
    },
  });

  // Disconnect from HighLevel
  const disconnectMutation = useMutation({
    mutationFn: () => apiClient.disconnectHighLevel(locationId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['highlevel-connection', locationId],
      });
      toast.success('Disconnected from CRM');
    },
    onError: (error) => {
      toast.error(`Failed to disconnect: ${error.message}`);
    },
  });

  return {
    connection,
    isConnected: !!connection?.isConnected,
    isLoading,
    error,
    connect: connectMutation.mutate,
    disconnect: disconnectMutation.mutate,
    isConnecting: connectMutation.isPending,
    isDisconnecting: disconnectMutation.isPending,
  };
}
